import React, { useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import { User } from 'lib/User';
import { AuthContext } from 'contexts/AuthContext';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';

import { EQUIPMENTS_URI, ORDER_HISTORY_URI } from 'constants/routes-constants';
import { ARCHIVED } from 'constants/states-app';

import { DEFAULT_CATEGORY_ID } from 'constants/categories-constants';

import { TotemCategory } from 'pages/Totem/TotemCategory';
import { TotemDashboard } from 'pages/Totem/TotemDashboard';
import { Equipments } from 'pages/Totem/TotemDashboard/Equipments';
import { OrderHistory } from 'pages/Totem/TotemDashboard/OrderHistory';
import { TotemNav } from 'pages/Totem/TotemNav';
import { TotemNavPlaceholder } from 'pages/Totem/TotemNavPlaceholder';
import { TotemExtendedProduct } from 'pages/Totem/TotemProducts/ExtendedProduct/ExtendedProduct';

import { EmojiLabel } from 'components/EmojiLabel';
import { FourOhFour } from 'components/404';
import { Loading } from 'components/Loading';
import { TotemButton } from 'ui/Button';

import { ErrorHandler } from 'utils/error-handler';

import {
    TotemStyle,
    TotemInactiveStyle,
    TotemInactiveHeading,
    TotemInactiveSpan,
    TotemLoadingStyle,
} from 'styles/pages/totem.styles';
import { parseQueryStrings } from 'utils';
import {
    OrderFragmentFragmentDoc,
    TotemFragmentFragment as TotemData,
    useGetCategoriesQuery,
    useGetOrdersOfTotemQuery,
    useReactivateTotemMutation,
} from 'data/__generated__';

import type { AuthContextType } from 'contexts/AuthContext';

interface TotemProps {
    totems: TotemData[];
}
export const Totem: React.FC<TotemProps> = ({ totems }) => {
    const {
        auth: { loading: authLoading },
    } = useContext(AuthContext) as AuthContextType;
    const { totemId, navItem, orderId, productId } = useParams();

    const { search } = useLocation();
    const { flow: queryFlow } = parseQueryStrings(search);
    const { cache } = useApolloClient();

    const currentTotem = totems?.find((totem) => totem?._id === totemId);

    // Hors des totems de l'utilisateur, la page répond 404 : interroger l'API ne ferait que provoquer un refus.
    const {
        loading: ordersLoading,
        error: ordersError,
        data: ordersData,
    } = useGetOrdersOfTotemQuery({
        variables: { totemId: totemId ?? '' },
        skip: !totemId || !currentTotem,
    });
    const {
        loading: categoriesLoading,
        error: categoriesError,
        data: categoriesData,
    } = useGetCategoriesQuery({
        variables: { orderId },
        skip: !currentTotem,
    });
    const [reactivateTotem, { loading: reactivateTotemLoading, error: reactivateTotemError }] =
        useReactivateTotemMutation();

    // Unauthorized totem (bookmark, shared link, access revoked): a 404, not the throw below that
    // would crash the page into the error boundary and report a fatal to Sentry.
    const isAccessDenied =
        !currentTotem ||
        [ordersError, categoriesError, reactivateTotemError].some((error) =>
            error?.graphQLErrors?.some(({ extensions }) => extensions?.code === 'AHEAD'),
        );

    if (isAccessDenied) {
        return <FourOhFour />;
    }

    const isCurrentTotemActive = (currentTotem?.state ?? '') !== ARCHIVED;

    // L'ApolloError elle-même : l'ErrorHandler y reconnaît une coupure réseau, qui n'est pas un bug.
    const queryError = ordersError ?? categoriesError ?? reactivateTotemError;
    if (queryError) {
        throw queryError;
    }

    const loading = authLoading || categoriesLoading || ordersLoading;

    const isRouteInsideOrderModification = navItem && ![EQUIPMENTS_URI, ORDER_HISTORY_URI].includes(navItem);

    if (loading) {
        return (
            <>
                {isRouteInsideOrderModification && (
                    <TotemStyle>
                        <TotemNavPlaceholder />
                    </TotemStyle>
                )}
                <TotemLoadingStyle>
                    <Loading />
                </TotemLoadingStyle>
            </>
        );
    }

    // Commande supprimée depuis (favori, autre onglet) : retour au totem plutôt qu'un écran d'erreur. Pas sur un
    // totem archivé : sans commande, l'analyste y est renvoyé vers une URL de commande, la redirection bouclerait.
    // Une commande tout juste créée est déjà dans le cache (résultat de la mutation) alors que la liste des commandes
    // n'est pas encore relue : elle n'est pas absente, juste pas encore listée.
    const recentOrderIds = ordersData?.ordersOfTotem?.recentOrders?.map((recentOrder) => recentOrder?._id);
    const isOrderInCache =
        !!orderId &&
        !!cache.readFragment({
            id: cache.identify({ __typename: 'Order', _id: orderId }),
            fragment: OrderFragmentFragmentDoc,
            fragmentName: 'OrderFragment',
        });
    if (isCurrentTotemActive && orderId && recentOrderIds && !recentOrderIds.includes(orderId) && !isOrderInCache) {
        return <Navigate to={`/totem/${totemId}`} replace />;
    }

    const ordersOfTotem = ordersData?.ordersOfTotem;

    const baseUrl = `/totem/${totemId}`;

    const categories = categoriesData?.clientCategories;
    // const { clientCategories: categories } = categoriesData;

    const { recentOrders } = ordersOfTotem || {};

    const order = (orderId && recentOrders?.find((recentOrder) => recentOrder?._id === orderId)) || null;
    const lastOrder = order && recentOrders?.find((recentOrder) => recentOrder?._id === order.previous_orderId);

    const categoriesIds = categories?.map((category) => category?._id);
    const defaultCategoryId = categoriesIds?.includes(DEFAULT_CATEGORY_ID) ? DEFAULT_CATEGORY_ID : categoriesIds?.[0];
    if (isCurrentTotemActive && !order && ![EQUIPMENTS_URI, ORDER_HISTORY_URI].includes(navItem ?? '')) {
        return (
            <TotemStyle>
                <TotemDashboard
                    ordersOfTotem={ordersOfTotem}
                    currentTotem={currentTotem}
                    baseUrl={baseUrl}
                    defaultCategoryId={defaultCategoryId}
                />
            </TotemStyle>
        );
    }

    const isAnalyst = User.isAnalyst(currentTotem?._id ?? '');

    const currentCategory = categories?.find((category) => category._id === navItem) || undefined;
    const categoryFlowSelected = queryFlow ?? currentCategory?.associatedFreefoodProductFlows?.[0];

    if (!currentCategory && isRouteInsideOrderModification) {
        return <Navigate to={`${baseUrl}/${defaultCategoryId}/${orderId}`} replace />;
    }

    if (!navItem && isAnalyst) {
        return <Navigate to={`${baseUrl}/${defaultCategoryId}/${orderId}`} />;
    }

    const handleRestoreTotem = async () => {
        if (!currentTotem?._id) return;
        await reactivateTotem({ variables: { totemId: currentTotem?._id } });
    };

    // TODO: FIND A WAY TO MAKE THIS WORK
    // unstable_usePrompt({
    //     message: "N'oubliez pas de valider votre commande pour réserver vos produits et assurer votre date de livraison !",
    //     when: ({ currentLocation, nextLocation }) =>
    //     order.state === INPROGRESS &&
    //     !categoriesIds.some((categoryId) => currentLocation.pathname.includes(categoryId)) &&
    //     isRouteInsideOrderModification &&
    //       currentLocation.pathname !== nextLocation.pathname,
    //   });
    return (
        <ErrorHandler type="totem">
            {isCurrentTotemActive ? (
                <TotemStyle>
                    {!navItem ? (
                        <TotemDashboard
                            ordersOfTotem={ordersOfTotem}
                            currentTotem={currentTotem}
                            baseUrl={baseUrl}
                            defaultCategoryId={defaultCategoryId}
                        />
                    ) : (
                        <>
                            {isRouteInsideOrderModification ? (
                                <>
                                    <TotemNav
                                        categories={categories}
                                        categoryFlowSelected={categoryFlowSelected}
                                        totemId={currentTotem._id}
                                        navItem={navItem}
                                        order={order}
                                    />
                                    {productId ? (
                                        <TotemExtendedProduct
                                            currentCategory={currentCategory}
                                            lastOrder={lastOrder}
                                            order={order}
                                        />
                                    ) : (
                                        <TotemCategory
                                            totemId={currentTotem._id}
                                            navItem={navItem}
                                            categoryFlowSelected={categoryFlowSelected}
                                            currentCategory={currentCategory}
                                            order={order}
                                            ordersOfTotem={ordersOfTotem}
                                            lastOrder={lastOrder}
                                            totems={totems}
                                        />
                                    )}
                                </>
                            ) : navItem === EQUIPMENTS_URI ? (
                                <Equipments currentTotem={currentTotem} />
                            ) : (
                                navItem === ORDER_HISTORY_URI && (
                                    <OrderHistory
                                        baseUrl={baseUrl}
                                        currentTotem={currentTotem}
                                        defaultCategoryId={defaultCategoryId}
                                        ordersOfTotem={ordersOfTotem}
                                    />
                                )
                            )}
                        </>
                    )}
                </TotemStyle>
            ) : (
                <TotemInactiveStyle>
                    <TotemInactiveHeading>
                        <EmojiLabel src="🔴" size="40px" /> <h2>TOTEM inactif</h2>
                    </TotemInactiveHeading>
                    <TotemInactiveSpan>
                        Vous pouvez réactiver à tout moment votre TOTEM
                        {totems.length > 1 ? ` #${currentTotem?.number}` : ''} pour la semaine prochaine.
                    </TotemInactiveSpan>
                    <TotemInactiveSpan>Cliquez sur le bouton ci-dessous.</TotemInactiveSpan>
                    <TotemButton onClick={handleRestoreTotem}>
                        {reactivateTotemLoading ? <ClipLoader color="white" size="16px" /> : 'Réactiver mon TOTEM'}
                    </TotemButton>
                </TotemInactiveStyle>
            )}
        </ErrorHandler>
    );
};
